import { useQuery } from '@tanstack/react-query';

import type { OnboardingStep } from './steps';

import { queryKeys } from '@/lib/queryKeys';


const ONBOARDING_STALE_TIME_MS = 5 * 60 * 1000;

/** Google Sheets published CSV — staging and production use separate sheets */
const SHEETS_URL: Record<string, string> = {
  production:
    'https://docs.google.com/spreadsheets/d/e/2PACX-1vSNvRezTHOj4dUM2398QxQfLDVwxPyRQ9rCS0kBM-V2u1QgWkEwnjZyXnJjEFbw73B3a1GZTPhsf_By/pub?gid=84417797&single=true&output=csv',
  staging:
    'https://docs.google.com/spreadsheets/d/e/2PACX-1vSNvRezTHOj4dUM2398QxQfLDVwxPyRQ9rCS0kBM-V2u1QgWkEwnjZyXnJjEFbw73B3a1GZTPhsf_By/pub?gid=84417797&single=true&output=csv',
};

const env = import.meta.env.PROD ? 'production' : 'staging';

function parseCsvRows(csv: string): string[][] {
  const rows: string[][] = [];
  let current = '';
  let inQuotes = false;
  let row: string[] = [];

  for (let i = 0; i < csv.length; i++) {
    const char = csv[i];
    if (inQuotes) {
      if (char === '"' && csv[i + 1] === '"') {
        current += '"';
        i++;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        current += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      row.push(current);
      current = '';
    } else if (char === '\r') {
      // skip carriage return
    } else if (char === '\n') {
      row.push(current);
      current = '';
      rows.push(row);
      row = [];
    } else {
      current += char;
    }
  }
  if (current || row.length > 0) {
    row.push(current);
    rows.push(row);
  }
  return rows;
}

function parseCsv(csv: string): OnboardingStep[] {
  const rows = parseCsvRows(csv);
  if (rows.length < 2) return [];

  const headers = rows[0].map((h) => h.trim().toLowerCase());
  const titleIdx = headers.indexOf('title');
  const descIdx = headers.indexOf('description');
  const videoIdx = headers.indexOf('mediaurl');

  if (titleIdx === -1 || descIdx === -1) return [];

  const steps: OnboardingStep[] = [];

  for (let i = 1; i < rows.length; i++) {
    const cols = rows[i];
    const title = (cols[titleIdx]?.trim() ?? '').replace(/\\n/g, '\n');
    const description = (cols[descIdx]?.trim() ?? '').replace(/\\n/g, '\n');
    const mediaUrl = (videoIdx !== -1 ? cols[videoIdx]?.trim() : '') ?? '';

    if (!title || !description) continue;

    steps.push({
      title,
      description,
      ...(mediaUrl ? { mediaUrl } : {}),
    });
  }

  return steps;
}

async function fetchSteps(): Promise<OnboardingStep[]> {
  const url = SHEETS_URL[env];
  const res = await fetch(url, { cache: 'no-cache' });
  if (!res.ok) throw new Error('Failed to fetch onboarding steps');
  const csv = await res.text();
  return parseCsv(csv);
}

export function useOnboardingSteps() {
  const { data: steps } = useQuery({
    queryKey: queryKeys.onboarding.steps,
    queryFn: fetchSteps,
    staleTime: ONBOARDING_STALE_TIME_MS,
    retry: 1,
  });

  return steps ?? [];
}
